import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { CalculoService } from '../../service/calculo.service';
import { CalculationResult } from '../../models/calculation-result.model';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';

type Nivel = 'SECCION' | 'DIVISION' | 'CLASE' | 'PRODUCTO';
type TipoReporte = 'INDICE' | 'VARIACION' | 'INCIDENCIA';

interface FilaIndice {
  _periodo: string;
  anio: number;
  mes: number;
  _ordenSeccion: number;
  _ordenDivision: number;
  _ordenClase: number;
  _ordenProducto: number;
  _ordenTipo: number;
  [key: string]: string | number;
}

@Component({
  selector: 'app-indices-report',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingOverlayComponent, AgGridAngular],
  templateUrl: './indices-report.html',
  styleUrl: './indices-report.scss'
})
export class IndicesReportComponent {
  readonly niveles: Nivel[] = ['SECCION', 'DIVISION', 'CLASE', 'PRODUCTO'];
  readonly nombresNivel: Record<Nivel, string> = {
    SECCION: 'Secciones', DIVISION: 'Divisiones', CLASE: 'Clases', PRODUCTO: 'Productos'
  };

  anioInicial = new Date().getFullYear();
  mesInicial = new Date().getMonth() + 1;
  anioFinal = new Date().getFullYear();
  mesFinal = new Date().getMonth() + 1;
  seleccionados = signal<Record<Nivel, boolean>>({
    SECCION: true, DIVISION: true, CLASE: true, PRODUCTO: true
  });
  filas = signal<FilaIndice[]>([]);
  cargando = signal(false);
  mensajeCarga = signal('Consultando índices...');
  mostrarAlterno = signal(true);
  mostrarGeneral = signal(true);
  mostrarAnterior = signal(true);
  incluirPonderaciones = signal(true);
  previsualizacionAbierta = signal(false);
  formato = signal<'XLSX' | 'CSV'>('XLSX');
  mensaje = signal('');
  readonly hayFilas = computed(() => this.filas().length > 0);
  readonly columnas = computed<ColDef[]>(() => this.camposReporte()
    .map(({ campo, titulo }) => ({ field: campo, headerName: titulo, minWidth: campo === 'nombre' ? 300 : 155, resizable: true, sortable: true, filter: true })));
  readonly columnasPorDefecto: ColDef = { sortable: true, filter: true, resizable: true };

  private readonly router = inject(Router);

  constructor(private calculoService: CalculoService) {}

  readonly tipoReporte = computed<TipoReporte>(() => this.router.url.includes('/reportes/incidencias') ? 'INCIDENCIA' :
    this.router.url.includes('/reportes/variacion') ? 'VARIACION' : 'INDICE');
  readonly tituloReporte = computed(() => this.tipoReporte() === 'INCIDENCIA' ? 'Reporte de incidencias' :
    this.tipoReporte() === 'VARIACION' ? 'Reporte de variación' : 'Reporte de índices');

  cambiarNivel(nivel: Nivel, activo: boolean): void {
    this.seleccionados.update(valor => ({ ...valor, [nivel]: activo }));
  }

  async previsualizar(): Promise<void> {
    const periodos = this.obtenerPeriodos();
    const niveles = this.niveles.filter(nivel => this.seleccionados()[nivel]);
    if (!periodos || !niveles.length) return;

    this.cargando.set(true);
    this.mensajeCarga.set('Consultando índices para la previsualización...');
    this.mensaje.set('');
    try {
      const resultado = await this.consultar(periodos, niveles);
      this.filas.set(resultado);
      this.previsualizacionAbierta.set(resultado.length > 0);
      this.mensaje.set(resultado.length
        ? `Vista previa: ${resultado.length} registro(s) encontrados.`
        : 'No se encontraron índices para el rango seleccionado.');
    } catch (error) {
      console.error('Error obteniendo reporte de índices', error);
      this.mensaje.set('No fue posible obtener la información de índices.');
    } finally {
      this.cargando.set(false);
    }
  }

  cerrarPrevisualizacion(): void {
    this.previsualizacionAbierta.set(false);
  }

  async exportar(): Promise<void> {
    const periodos = this.obtenerPeriodos();
    const niveles = this.niveles.filter(nivel => this.seleccionados()[nivel]);
    if (!periodos || !niveles.length) return;

    this.cargando.set(true);
    this.mensajeCarga.set('Generando el archivo Excel...');
    this.mensaje.set('');
    try {
      const resultado = await this.consultar(periodos, niveles);
      this.filas.set(resultado);
      if (!resultado.length) {
        this.mensaje.set('No hay información para exportar.');
        return;
      }
      if (this.formato() === 'CSV') {
        this.descargarCsv(resultado);
        this.mensaje.set('Reporte CSV generado correctamente.');
        return;
      }
      const libro = new ExcelJS.Workbook();
      // La primera hoja replica la grid de previsualización, con todos los períodos.
      this.agregarHoja(libro, 'Previsualización', resultado);
      periodos.forEach(periodo => {
        const etiquetaPeriodo = `${periodo.anio}-${String(periodo.mes).padStart(2, '0')}`;
        this.agregarHoja(
          libro,
          periodos.length === 1 ? this.nombreHojaBase() : `${this.nombreHojaBase()}_${etiquetaPeriodo}`,
          resultado.filter(fila => fila._periodo === etiquetaPeriodo)
        );
      });
      const contenido = await libro.xlsx.writeBuffer();
      const enlace = document.createElement('a');
      const url = URL.createObjectURL(new Blob([contenido], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      enlace.href = url;
      enlace.download = `reporte_${this.tipoReporte().toLowerCase()}_${this.anioInicial}-${String(this.mesInicial).padStart(2, '0')}_a_${this.anioFinal}-${String(this.mesFinal).padStart(2, '0')}.xlsx`;
      enlace.style.display = 'none';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
      this.mensaje.set('Reporte generado correctamente.');
    } catch (error) {
      console.error('Error exportando reporte de índices', error);
      this.mensaje.set('No fue posible generar el reporte.');
    } finally {
      this.cargando.set(false);
    }
  }

  private async consultar(periodos: Array<{ anio: number; mes: number }>, niveles: Nivel[]): Promise<FilaIndice[]> {
    const respuestas = await Promise.all(periodos.flatMap(periodo => niveles.map(async nivel => {
      const datos = await firstValueFrom(this.calculoService.ejecutarCalculo({
        anio: periodo.anio, mes: periodo.mes, tipoCalculo: nivel,
        fkSeccion: 0, fkDivision: 0, fkClase: 0
      }));
      return (datos || []).map(dato => this.mapearFila(dato, nivel, periodo));
    })));
    return respuestas.flat().sort((a, b) =>
      a._periodo.localeCompare(b._periodo) || a._ordenSeccion - b._ordenSeccion ||
      a._ordenDivision - b._ordenDivision || a._ordenClase - b._ordenClase ||
      a._ordenTipo - b._ordenTipo || a._ordenProducto - b._ordenProducto);
  }

  private mapearFila(dato: CalculationResult, nivel: Nivel, periodo: { anio: number; mes: number }): FilaIndice {
    const ordenTipo = nivel === 'SECCION' ? 1 : nivel === 'DIVISION' ? 2 : nivel === 'CLASE' ? 3 : 4;
    return {
      _periodo: `${periodo.anio}-${String(periodo.mes).padStart(2, '0')}`,
      anio: periodo.anio,
      mes: periodo.mes,
      _ordenSeccion: dato.fkSeccion ?? 0, _ordenDivision: dato.fkDivision ?? 0,
      _ordenClase: dato.fkClase ?? 0, _ordenProducto: dato.fkProducto ?? 0, _ordenTipo: ordenTipo,
      tipo: nivel,
      codigo_ine: nivel === 'SECCION' ? dato.codigoIneSeccion ?? dato.codigoIne ?? '' :
        nivel === 'DIVISION' ? dato.codigoIneDivision ?? dato.codigoIne ?? '' :
        nivel === 'CLASE' ? dato.codigoIneClase ?? dato.codigoIne ?? '' : dato.codigoIne ?? '',
      nombre: nivel === 'SECCION' ? dato.nombreSeccion ?? '' : nivel === 'DIVISION' ? dato.nombreDivision ?? '' :
        nivel === 'CLASE' ? dato.nombreClase ?? '' : dato.nombreProducto ?? '',
      fk_seccion: dato.codigoIneSeccion ?? '', fk_division: dato.codigoIneDivision ?? '',
      fk_clase: dato.codigoIneClase ?? '', fk_producto: dato.codigoIne ?? '',
      indice_anterior: dato.indiceAnterior ?? '', indice: dato.indice ?? '',
      indice_anterior_importado: dato.indiceAnteriorImportado ?? '', indice_importado: dato.indiceImportado ?? '',
      indice_anterior_general: dato.indiceAnteriorGeneral ?? '', indice_general: dato.indiceGeneral ?? '',
      ponderacion: dato.ponderacion ?? '', ponderacion_alterno: dato.ponderacionImportado ?? '',
      ponderacion_general: dato.ponderacionGeneral ?? '', variacion: dato.variacion ?? '',
      variacion_alterno: dato.variacionAlterno ?? '', variacion_general: dato.variacionGeneral ?? '', incidencia: dato.incidencia ?? '',
      incidencia_alterno: dato.incidenciaAlterno ?? '', incidencia_general: dato.incidenciaGeneral ?? '',
      incidencia_anterior: '', incidencia_anterior_alterno: '', incidencia_anterior_general: ''
    };
  }

  private agregarHoja(libro: ExcelJS.Workbook, nombreHoja: string, filas: FilaIndice[]): void {
    const hoja = libro.addWorksheet(nombreHoja);
    const campos = this.camposReporte();
    const datos = filas.map(fila => Object.fromEntries(campos.map(({ campo, titulo }) => [titulo, fila[campo]])));
    if (!datos.length) return;
    hoja.columns = Object.keys(datos[0]).map(key => ({ header: key, key, width: Math.max(key.length + 5, 18) }));
    hoja.addRows(datos);
    hoja.views = [{ state: 'frozen', ySplit: 1 }];
    const ultimaColumna = hoja.getRow(1).cellCount;
    hoja.autoFilter = { from: 'A1', to: `${this.letraColumna(ultimaColumna)}1` };
  }

  private letraColumna(numero: number): string {
    let resultado = '';
    while (numero > 0) { numero--; resultado = String.fromCharCode(65 + numero % 26) + resultado; numero = Math.floor(numero / 26); }
    return resultado;
  }

  private camposReporte(): Array<{ campo: string; titulo: string }> {
    const columnas: Array<{ campo: string; titulo: string }> = [
      { campo: 'anio', titulo: 'AÑO' },
      { campo: 'mes', titulo: 'MES' },
      { campo: 'tipo', titulo: 'NIVEL' },
      { campo: 'codigo_ine', titulo: 'CODIGO INE' },
      { campo: 'nombre', titulo: 'DESCRIPCIÓN' }
    ];
    if (this.incluirPonderaciones()) {
      columnas.push({ campo: 'ponderacion', titulo: 'PONDERACION' });
      if (this.mostrarAlterno()) columnas.push({ campo: 'ponderacion_alterno', titulo: 'PONDERACION ALTERNO' });
      if (this.mostrarGeneral()) columnas.push({ campo: 'ponderacion_general', titulo: 'PONDERACION GENERAL' });
    }
    if (this.tipoReporte() === 'VARIACION') {
      columnas.push({ campo: 'variacion', titulo: 'VARIACION' });
      if (this.mostrarAlterno()) columnas.push({ campo: 'variacion_alterno', titulo: 'VARIACION ALTERNA' });
      if (this.mostrarGeneral()) columnas.push({ campo: 'variacion_general', titulo: 'VARIACION GENERAL' });
    } else if (this.tipoReporte() === 'INCIDENCIA') {
      columnas.push({ campo: 'incidencia', titulo: 'INC. INDICE' });
      if (this.mostrarAlterno()) columnas.push({ campo: 'incidencia_alterno', titulo: 'INC. INDICE ALTERNO' });
      if (this.mostrarGeneral()) columnas.push({ campo: 'incidencia_general', titulo: 'INC. INDICE GENERAL' });
    } else {
      columnas.push({ campo: 'indice', titulo: 'INDICE' });
      if (this.mostrarAlterno()) columnas.push({ campo: 'indice_importado', titulo: 'INDICE ALTERNO' });
      if (this.mostrarGeneral()) columnas.push({ campo: 'indice_general', titulo: 'INDICE GENERAL' });
      if (!this.mostrarAnterior()) return columnas;
      columnas.push({ campo: 'indice_anterior', titulo: 'INDICE ANTERIOR' });
      if (this.mostrarAlterno()) columnas.push({ campo: 'indice_anterior_importado', titulo: 'INDICE ANTERIOR ALTERNO' });
      if (this.mostrarGeneral()) columnas.push({ campo: 'indice_anterior_general', titulo: 'INDICE ANTERIOR GENERAL' });
    }
    return columnas;
  }

  private nombreHojaBase(): string {
    return this.tipoReporte() === 'INCIDENCIA' ? 'Incidencias' : this.tipoReporte() === 'VARIACION' ? 'Variacion' : 'Indices';
  }

  private descargarCsv(filas: FilaIndice[]): void {
    const campos = this.camposReporte();
    const escapar = (valor: unknown) => `"${String(valor ?? '').replaceAll('"', '""')}"`;
    const contenido = '\ufeff' + [campos.map(c => escapar(c.titulo)).join(','), ...filas.map(fila => campos.map(c => escapar(fila[c.campo])).join(','))].join('\r\n');
    const url = URL.createObjectURL(new Blob([contenido], { type: 'text/csv;charset=utf-8' }));
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `reporte_${this.tipoReporte().toLowerCase()}.csv`;
    enlace.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }

  private obtenerPeriodos(): Array<{ anio: number; mes: number }> | null {
    const inicio = this.anioInicial * 12 + this.mesInicial - 1;
    const fin = this.anioFinal * 12 + this.mesFinal - 1;
    if (this.mesInicial < 1 || this.mesInicial > 12 || this.mesFinal < 1 || this.mesFinal > 12 || inicio > fin) {
      this.mensaje.set('El rango de año y mes no es válido.');
      return null;
    }
    const periodos: Array<{ anio: number; mes: number }> = [];
    for (let valor = inicio; valor <= fin; valor++) periodos.push({ anio: Math.floor(valor / 12), mes: valor % 12 + 1 });
    return periodos;
  }
}
